import { useContext, useEffect, useState } from 'react';

import { StaticDateRangeSelect } from '@/components/common/date/StaticDateRangeSelect';
import { DetailedRoute } from '@/generated/model/detailedRoute';
import { ctxPopup } from '@/contexts/PopupContext';
import { ctxRoutes } from '@/contexts/RoutesCtx.tsx';
import { rethrowIfNotClientException } from '@/utilities/throwableUtils';
import { RoutesNoResult } from '@/components/routes/RoutesNoResult';
import { MinimalTarget } from '@/generated/model';
import { useRoutesQuery } from '@/components/routes/api/useRoutesQuery';

type SubmittedRouteRequest = {
  targetId: number;
  from: Date;
  to: Date;
};

export function RoutesDateSelect({
  selectedTarget,
  setRoutes,
  setSelectedDates,
  openTargetSelector,
}: {
  selectedTarget: MinimalTarget | undefined;
  setRoutes: (values: DetailedRoute[] | undefined) => void;
  setSelectedDates: (from: Date, to: Date) => void;
  openTargetSelector?: () => void;
}) {
  const [submittedRequest, setSubmittedRequest] = useState<SubmittedRouteRequest>();
  const ctxPopups = useContext(ctxPopup);
  const ctxRoute = useContext(ctxRoutes);
  const showLoader = ctxPopups?.showLoader;
  const hideLoader = ctxPopups?.hideLoader;
  const setContextSelectedDates = ctxRoute?.setSelectedDates;
  const setPositions = ctxRoute?.setPositions;
  const setActualToTime = ctxRoute?.setActialToTime;
  const setBottomSheetOpen = ctxRoute?.setIsBottomSheetOpen;
  const activeRequest =
    selectedTarget && submittedRequest?.targetId === selectedTarget.id ? submittedRequest : undefined;
  const query = useRoutesQuery({ targetId: selectedTarget?.id, dates: activeRequest });
  const hasEmptyLoadedRoutes = Boolean(
    selectedTarget &&
    ctxRoute?.selectedTarget?.id === selectedTarget.id &&
    ctxRoute.selectedDates &&
    ctxRoute.routes?.length === 0 &&
    !ctxRoute.routesLoading
  );
  const noRoutes = Boolean(
    hasEmptyLoadedRoutes || (activeRequest && query.isSuccess && !query.isFetching && query.data?.routes.length === 0)
  );

  useEffect(() => {
    if (!query.isFetching) {
      return;
    }
    showLoader?.('circular');
    return () => hideLoader?.();
  }, [hideLoader, query.isFetching, showLoader]);

  useEffect(() => {
    if (query.error) {
      rethrowIfNotClientException(query.error);
    }
  }, [query.error]);

  useEffect(() => {
    if (!activeRequest || !query.isSuccess || query.isFetching || !query.data) {
      return;
    }

    setContextSelectedDates?.({ from: activeRequest.from, to: activeRequest.to });
    setPositions?.(query.data.statuses);
    setActualToTime?.(query.data.actualToTime);

    if (query.data.routes.length === 0) {
      setBottomSheetOpen?.(false);
      return;
    }

    setRoutes(query.data.routes);
    setSelectedDates(activeRequest.from, activeRequest.to);
  }, [
    activeRequest,
    query.data,
    query.isFetching,
    query.isSuccess,
    setActualToTime,
    setBottomSheetOpen,
    setContextSelectedDates,
    setPositions,
    setRoutes,
    setSelectedDates,
  ]);

  function setValuesWrapper(from: Date, to: Date) {
    if (!selectedTarget || !from || !to) {
      setSubmittedRequest(undefined);
      return;
    }

    if (
      activeRequest?.targetId === selectedTarget.id &&
      activeRequest.from.getTime() === from.getTime() &&
      activeRequest.to.getTime() === to.getTime()
    ) {
      void query.refetch();
      return;
    }

    setSubmittedRequest({ targetId: selectedTarget.id, from, to });
  }

  function chooseNewVehicle() {
    openTargetSelector?.();
    setSubmittedRequest(undefined);
    ctxRoute?.setSelectedTarget(undefined);
    ctxRoute?.setTab(0);
  }

  function chooseNewDate() {
    openTargetSelector?.();
    setSubmittedRequest(undefined);
    ctxRoute?.setSelectedDates(undefined);
  }

  return (
    <>
      {ctxRoute?.selectedDates && noRoutes ? (
        <RoutesNoResult
          onChooseNewDate={chooseNewDate}
          onChooseNewVehicle={chooseNewVehicle}
          targetName={selectedTarget?.name ?? ''}
        />
      ) : (
        <StaticDateRangeSelect
          setSelectedDates={setValuesWrapper}
          // currentValue={{ from: ctxRoute?.selectedDates?.from ?? null, to: ctxRoute?.selectedDates?.to ?? null }}
        />
      )}
    </>
  );
}
